import { useCallback, useEffect, useMemo, useState } from 'react';
import type {
  ColumnFiltersState,
  PaginationState,
  SortingState,
} from '@tanstack/react-table';
import type {
  ManualFiltering,
  ManualPagination,
  ManualSorting,
} from '../types';
import { usePersistentState } from './usePersistentState';
import { useDebouncedValue } from '../../../hooks/useDebouncedValue';

const DEFAULT_PAGE_SIZE = 10;
const DEFAULT_SEARCH_DEBOUNCE_MS = 300;

export type ServerPaginationOptions = {
  manual: true;
  initialPageSize?: number;
};

export type ServerSortingOptions = {
  manual: true;
  initial?: SortingState;
};

export type ServerFilteringOptions = {
  manual: true;
  initialColumnFilters?: ColumnFiltersState;
  /** Pause in typing before `requestParams.search` changes. Defaults to 300 ms. */
  searchDebounceMs?: number;
};

export type UseServerTableStateOptions = {
  /** What the server does. A part that is left out stays in the browser. */
  pagination?: ServerPaginationOptions;
  sorting?: ServerSortingOptions;
  filtering?: ServerFilteringOptions;
  /**
   * Remembers the state across visits: sorting, filters and page size in
   * localStorage, like `Table` does for its own state; the search in
   * sessionStorage, so a term typed today does not greet next week's visit.
   */
  storageKey?: string;
  /**
   * Wraps every change that alters the request, e.g. React's `startTransition`.
   * Lets a table under Suspense keep its rows while the next page loads.
   */
  startTransition?: (callback: () => void) => void;
};

type PaginationParams = {
  pageIndex: number;
  pageSize: number;
  offset: number;
};
type SortingParams = { sorting: SortingState };
type FilteringParams = {
  /** The search once typing paused; the input itself shows every keystroke. */
  search: string;
  columnFilters: ColumnFiltersState;
};

type IfSet<Option, Value> = Option extends object ? Value : unknown;

export type ServerTableState<Options extends UseServerTableStateOptions> = {
  /** Values to build the request from. Only the server-side parts are present. */
  requestParams: IfSet<Options['pagination'], PaginationParams> &
    IfSet<Options['sorting'], SortingParams> &
    IfSet<Options['filtering'], FilteringParams>;
  /** Spread onto `<Table />`. Only the server-side parts are present. */
  tableProps: IfSet<
    Options['pagination'],
    { manualPagination: ManualPagination }
  > &
    IfSet<Options['sorting'], { manualSorting: ManualSorting }> &
    IfSet<Options['filtering'], { manualFiltering: ManualFiltering }>;
};

type StoredState = {
  pageSize: number;
  sorting: SortingState;
  columnFilters: ColumnFiltersState;
};

/** Keeps only stored fields of the right shape; anything else falls back. */
const restoreStoredState = (
  storedState: Partial<StoredState>,
  initialState: StoredState,
): StoredState => ({
  pageSize:
    typeof storedState.pageSize === 'number' && storedState.pageSize > 0
      ? storedState.pageSize
      : initialState.pageSize,
  sorting: Array.isArray(storedState.sorting)
    ? storedState.sorting
    : initialState.sorting,
  columnFilters: Array.isArray(storedState.columnFilters)
    ? storedState.columnFilters
    : initialState.columnFilters,
});

/**
 * Owns the table state that a server request is built from: page, sort order,
 * column filters and a debounced search. Returns to page one together with
 * any change to the search, filters or sorting, so each sends one request.
 * Hands the matching `manual*` props to `Table`, which keeps the page index in
 * bounds once the total is known. The total row count is not part of
 * this: it comes from the response, so the caller passes it to `Table` as
 * `rowCount`.
 */
export function useServerTableState<
  const Options extends UseServerTableStateOptions,
>(options: Options): ServerTableState<Options> {
  const { pagination, sorting, filtering, storageKey, startTransition } =
    options;
  const transition = useCallback(
    (callback: () => void) =>
      startTransition ? startTransition(callback) : callback(),
    [startTransition],
  );

  const [stored, setStored] = usePersistentState<StoredState>(
    storageKey,
    {
      pageSize: pagination?.initialPageSize ?? DEFAULT_PAGE_SIZE,
      sorting: sorting?.initial ?? [],
      columnFilters: filtering?.initialColumnFilters ?? [],
    },
    restoreStoredState,
  );
  const [search, setSearch] = usePersistentState(
    storageKey && `${storageKey}_search`,
    '',
    (storedSearch, initialSearch) =>
      typeof storedSearch === 'string' ? storedSearch : initialSearch,
    'session',
  );
  const [pageIndex, setPageIndex] = useState(0);
  const [appliedSearch, setAppliedSearch] = useState(search);
  const debouncedSearch = useDebouncedValue(
    search,
    filtering?.searchDebounceMs ?? DEFAULT_SEARCH_DEBOUNCE_MS,
  );
  // Applied in an effect so the commit can run inside the transition. The
  // page resets together with it, so the request goes out once.
  useEffect(() => {
    if (debouncedSearch === appliedSearch) return;
    transition(() => {
      setAppliedSearch(debouncedSearch);
      setPageIndex(0);
    });
  }, [debouncedSearch, appliedSearch, transition]);

  const manualPagination = useMemo<ManualPagination>(
    () => ({
      pagination: { pageIndex, pageSize: stored.pageSize },
      onPaginationChange: (next: PaginationState) =>
        transition(() => {
          setPageIndex(next.pageIndex);
          setStored((current) =>
            current.pageSize === next.pageSize
              ? current
              : { ...current, pageSize: next.pageSize },
          );
        }),
    }),
    [pageIndex, stored.pageSize, transition, setStored],
  );

  const manualSorting = useMemo<ManualSorting>(
    () => ({
      sorting: stored.sorting,
      onSortingChange: (next) =>
        transition(() => {
          setStored((current) => ({ ...current, sorting: next }));
          setPageIndex(0);
        }),
    }),
    [stored.sorting, transition, setStored],
  );

  const manualFiltering = useMemo<ManualFiltering>(
    () => ({
      search,
      // Keystrokes only move the input; the debounce commits them.
      onSearchChange: setSearch,
      columnFilters: stored.columnFilters,
      onColumnFiltersChange: (next) =>
        transition(() => {
          setStored((current) => ({ ...current, columnFilters: next }));
          setPageIndex(0);
        }),
    }),
    [search, setSearch, stored.columnFilters, transition, setStored],
  );

  // Booleans, not the option objects: those are new literals on every render
  // and would give the result a new identity each time.
  const hasPagination = !!pagination;
  const hasSorting = !!sorting;
  const hasFiltering = !!filtering;

  return useMemo(() => {
    const requestParams = {
      ...(hasPagination && {
        pageIndex,
        pageSize: stored.pageSize,
        offset: pageIndex * stored.pageSize,
      }),
      ...(hasSorting && { sorting: stored.sorting }),
      ...(hasFiltering && {
        search: appliedSearch,
        columnFilters: stored.columnFilters,
      }),
    };
    const tableProps = {
      ...(hasPagination && { manualPagination }),
      ...(hasSorting && { manualSorting }),
      ...(hasFiltering && { manualFiltering }),
    };
    return { requestParams, tableProps } as ServerTableState<Options>;
  }, [
    hasPagination,
    hasSorting,
    hasFiltering,
    pageIndex,
    stored,
    appliedSearch,
    manualPagination,
    manualSorting,
    manualFiltering,
  ]);
}
